import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { round } from '@authority-labs/helpers';
import { EChartsOption, SeriesOption } from 'echarts';

export class WaterSupplyData {
  pressureAtFireFlowAt_0psi = 0;
  pressureAtFireFlowAt_20psi = 20;
  constructor(
    public staticPressure: number,
    public residualPressure: number,
    public totalFlow: number,
    public systemFlow: number,
    public systemDemand: number
  ) {}
  get expectedFlowAt_0psi(): number {
    return Math.round(
      (this.totalFlow *
        Math.pow(this.staticPressure - this.pressureAtFireFlowAt_0psi, 0.54)) /
        Math.pow(this.staticPressure - this.residualPressure, 0.54)
    );
  }

  get expectedFlowAt_20psi(): number {
    return Math.round(
      (this.totalFlow *
        Math.pow(this.staticPressure - this.pressureAtFireFlowAt_20psi, 0.54)) /
        Math.pow(this.staticPressure - this.residualPressure, 0.54)
    );
  }

  get availablePressureAtDemandPoint(): number {
    return Number(
      (
        this.staticPressure -
        Math.pow(
          (this.systemFlow / this.totalFlow) *
            Math.pow(this.staticPressure - this.residualPressure, 0.54),
          1 / 0.54
        )
      ).toFixed(1)
    );
  }

  get safetyProvided(): number {
    return this.availablePressureAtDemandPoint - this.systemDemand;
  }

  get safetyProvidedPercentage(): number {
    return Math.round(
      100 *
        round(
          Number(this.safetyProvided) /
            Number(this.availablePressureAtDemandPoint),
          3
        )
    );
  }
}

@Component({
  selector: 'authority-labs-water-supply-chart',
  templateUrl: './water-supply-chart.component.html',
  styleUrls: ['./water-supply-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WaterSupplyChartComponent implements OnInit {
  @Input() staticPressure: number;
  @Input() residualPressure: number;
  @Input() totalFlow: number;
  @Input() systemFlow: number;
  @Input() systemDemand: number;

  waterSupplyData: WaterSupplyData;
  chartOption: EChartsOption;

  constructor() {}

  ngOnInit(): void {
    this.waterSupplyData = new WaterSupplyData(
      this.staticPressure,
      this.residualPressure,
      this.totalFlow,
      this.systemFlow,
      this.systemDemand
    );
    this.setChartOptions();
  }

  toFixed(num: number): number {
    return Number(num.toFixed(1));
  }

  private setChartOptions(): void {
    const real = [...Array(11).keys()].map(
      (x) =>
        (x *
          (this.waterSupplyData.expectedFlowAt_0psi > 2500
            ? round(this.waterSupplyData.expectedFlowAt_0psi, -3)
            : round(this.waterSupplyData.expectedFlowAt_0psi, -2))) /
        10
    );

    const lines: SeriesOption[] = real.map((x) => ({
      type: 'line',
      color: 'lightgrey',
      data: [
        {
          value: [Math.round(Math.pow(x, 1.85)), 0],
          symbolSize: 0,
          label: {
            show: true,
            offset: [0, 30],
            formatter: () => (x ? x.toString() : '')
          }
        },
        {
          value: [
            Math.round(Math.pow(x, 1.85)),
            Math.ceil(this.staticPressure / 10) * 10 + 20
          ],
          symbol: 'none'
        }
      ]
    }));

    const lineParams: SeriesOption = {
      type: 'line',
      symbol: 'circle',
      symbolSize: 10,
      lineStyle: {
        type: 'dotted'
      }
    };

    const blueLine: SeriesOption = {
      ...lineParams,
      color: 'blue',
      data: [
        {
          value: [0, this.staticPressure],
          label: {
            show: true,
            offset: [20, 0],
            formatter: () => this.staticPressure + ', 0'
          }
        },
        {
          value: [Math.pow(this.totalFlow, 1.85), this.residualPressure],
          label: {
            show: true,
            offset: [20, 0],
            formatter: () => this.residualPressure + ', ' + this.totalFlow
          }
        },
        {
          value: [
            Math.pow(this.waterSupplyData.expectedFlowAt_0psi, 1.85),
            this.waterSupplyData.pressureAtFireFlowAt_0psi
          ],
          label: {
            show: true,
            offset: [20, 0],
            formatter: () =>
              this.waterSupplyData.expectedFlowAt_0psi +
              ', ' +
              this.waterSupplyData.pressureAtFireFlowAt_0psi
          }
        }
      ]
    };

    const redLine: SeriesOption = {
      ...lineParams,
      color: 'red',
      data: [
        {
          value: [0, 0]
        },
        {
          value: [Math.pow(this.systemFlow, 1.85), this.systemDemand],
          label: {
            show: true,
            offset: [25, 0],
            formatter: () => this.systemDemand + ', ' + this.systemFlow
          }
        }
      ]
    };

    const greenLine: SeriesOption = {
      ...lineParams,
      color: 'green',
      symbol: 'emptyCircle',
      data: [
        {
          value: [Math.pow(this.systemFlow, 1.85), this.systemDemand],
          symbolSize: 0
        },
        {
          value: [
            Math.pow(this.systemFlow, 1.85),
            this.waterSupplyData.availablePressureAtDemandPoint
          ],
          label: {
            show: true,
            offset: [60, -50],
            // position: 'outside',
            color: 'green',
            formatter: () =>
              this.systemDemand < 5
                ? ''
                : 'Safety: ' +
                  round(this.waterSupplyData.safetyProvided, 1) +
                  ' psi / ' +
                  this.waterSupplyData.safetyProvidedPercentage +
                  '%'
          }
        }
      ]
    };

    this.chartOption = {
      animation: false,
      grid: {
        show: true,
        borderColor: 'black',
        left: 40,
        top: 20,
        right: 40,
        bottom: 40
        // borderWidth: 1
      },
      yAxis: {
        name: '(PSI) לחץ',
        nameLocation: 'middle',
        nameGap: 25,
        max: Math.ceil(this.staticPressure / 10) * 10 + 20,
        splitLine: {
          show: true
        },
        minorSplitLine: {
          show: true
        },
        nameTextStyle: {
          fontWeight: 'bold'
          // color: 'rgba(170, 23, 23, 1)'
        }
      },
      xAxis: {
        type: 'value',
        name: '(GPM) ספיקה',
        nameLocation: 'middle',
        nameGap: 30,
        splitLine: {
          show: false
        },
        axisLabel: {
          show: false
        },
        axisTick: {
          show: false
        },
        nameTextStyle: {
          fontWeight: 'bold'
          // color: 'rgba(170, 23, 23, 1)'
        }
      },
      series: [blueLine, redLine, greenLine, ...lines]
    };
  }
}
